import { createRouter, createWebHistory, RouterView } from 'vue-router'
import type { RouteLocationGeneric, RouteLocationNormalized } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useSubscriptionAccessStore } from '@/stores/subscriptionAccess'
import {
  getRequiredPlatformForRoute,
  shouldSkipPlatformAccessCheck
} from '@/router/platformAccessGuard'
import posthog from 'posthog-js'

import RegisterView from '@/views/auth/RegisterView.vue'
import LoginView from '@/views/auth/LoginView.vue'
import ForgotPasswordView from '@/views/auth/ForgotPasswordView.vue'
import AuthCallbackView from '@/views/auth/AuthCallbackView.vue'
import CompleteProfileView from '@/views/auth/CompleteProfileView.vue'
import SettingsView from '@/views/settings/SettingsView.vue'
import DashboardLayout from '@/components/layout/DashboardLayout.vue'
import DashboardIndexView from '@/views/dashboard/DashboardIndexView.vue'
import AccountView from '@/views/account/AccountView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import PermissionDeniedView from '@/views/PermissionDeniedView.vue'

import ManageIndexView from '@/views/manage/ManageIndexView.vue'
import ManageExpeditionView from '@/views/manage/expedition/ManageExpeditionView.vue'
import ManageExpeditionUserView from '@/views/manage/expedition/ManageExpeditionUserView.vue'
import CreateExpeditionView from '@/views/manage/CreateExpeditionView.vue'
import CreatePodView from '@/views/manage/CreatePodView.vue'
import OnboardingView from '@/views/manage/OnboardingView.vue'
import ManageExpeditionMembersView from '@/views/manage/expedition/ExpeditionMembersView.vue'
import ManageExpeditionCurriculumView from '@/views/manage/expedition/ExpeditionCurriculumView.vue'
import ManageExpeditionNotesView from '@/views/manage/expedition/ExpeditionNotesView.vue'
import ManageAllReportsView from '@/views/manage/all-fellows/ManageAllReportsView.vue'

import TermsAndConditionsView from '@/views/auth/TermsAndConditionsView.vue'
import ResetPasswordView from '@/views/auth/ResetPasswordView.vue'
import PrivacyPolicyView from '@/views/auth/PrivacyPolicyView.vue'
import FellowReviewHistoryView from '@/views/manage/fellow/FellowReviewHistoryView.vue'
import FellowSettingsView from '@/views/manage/fellow/FellowSettingsView.vue'

import UserExpeditionView from '@/views/dashboard/UserExpeditionView.vue'
import DemoModeView from '@/views/manage/DemoModeView.vue'
import ManageAllAdventurersView from '@/views/manage/all-fellows/ManageAllAdventurersView.vue'
import ProgramStatusesView from '@/views/manage/all-fellows/ProgramStatusesView.vue'
import ManageAllInternalCommentsView from '@/views/manage/all-fellows/ManageAllInternalCommentsView.vue'
import SubscriptionView from '@/views/subscription/SubscriptionView.vue'
import SubscriptionManageView from '@/views/subscription/SubscriptionManageView.vue'

import PodManagementLayout from '@/components/layout/PodManagementLayout.vue'
import RouterViewWrapper from '@/components/layout/RouterViewWrapper.vue'
import ManageLayout from '@/components/layout/ManageLayout.vue'
import ExpeditionProgramStatusesView from '@/views/manage/expedition/ExpeditionProgramStatusesView.vue'
import ExpeditionReportsView from '@/views/manage/expedition/ExpeditionReportsView.vue'
import ExpeditionCommentsView from '@/views/manage/expedition/ExpeditionCommentsView.vue'
import ExpeditionJobApplicationsView from '@/views/manage/expedition/ExpeditionJobApplicationsView.vue'

import CampusLayout from '@/components/layout/CampusLayout.vue'
import ProfileAccountLayout from '@/components/layout/ProfileAccountLayout.vue'

import { getRoleFromMultipleSources, getRoleFromCookie } from '@/utils/roleHelpers'
import { APP_ROUTES } from '@/const/appRoutes'
import { useAuthStore } from '@/stores/auth'
import { savePostLoginRedirect } from '@/utils/authRedirect'

// Roles that have management access (can access /manage routes)
const MANAGEMENT_ROLES: string[] = ['GUIDE', 'MENTOR']

/**
 * Get user role from userStore or JWT token or API
 * Uses centralized helper function to avoid code duplication
 */
async function getUserRole(userStore: ReturnType<typeof useUserStore>): Promise<string> {
  const role = await getRoleFromMultipleSources(userStore)
  return role || ''
}

/**
 * Check if a role has management access (GUIDE or MENTOR)
 */
function hasManagementAccess(role: string): boolean {
  return MANAGEMENT_ROLES.includes(role.toUpperCase())
}

/**
 * Check if user is an admin.
 * Reads from the auth store, which is hydrated from localStorage on creation.
 */
function isUserAdmin(): boolean {
  try {
    return useAuthStore().isAdmin
  } catch {
    return false
  }
}

const routes = [
  {
    path: '/',
    name: 'root',
    component: DashboardLayout,
    meta: {
      title: 'Dashboard'
    },
    // eslint-disable-next-line complexity
    beforeEnter: async (to: any, from: any, next: any) => {
      const userStore = useUserStore()

      // Check authentication via userStore.currentUser first (set after login)
      // If not available, try to fetch it (works with cookies even if no token in localStorage)
      if (!userStore.currentUser) {
        try {
          await userStore.fetchCurrentUser()
        } catch (err) {
          console.error('Failed to fetch user data:', err)
          // If fetch fails with auth error, redirect to login
          // But if it's a network error, we might still be authenticated via cookies
          if (err && typeof err === 'object' && 'response' in err) {
            const httpErr = err as any
            if (httpErr.response?.status === 401 || httpErr.response?.status === 403) {
              // Clear any invalid tokens
              localStorage.removeItem('token')
              localStorage.removeItem('refresh_token')
              return next('/login')
            }
          }
          // For other errors (network, etc.), check if we have a token as fallback
          const token = localStorage.getItem('token')
          if (!token) {
            return next('/login')
          }
          // If we have a token but fetch failed, continue anyway (might be temporary network issue)
        }
      }

      // If we still don't have user data after trying to fetch, check token
      if (!userStore.currentUser) {
        const token = localStorage.getItem('token')
        if (!token) {
          return next('/login')
        }
        // If we have token but no user data, something is wrong - redirect to login
        return next('/login')
      }

      // Get user role with detailed logging for debugging
      const userRole = await getUserRole(userStore)

      // Debug logging to help identify role detection issues
      console.debug('[Router] Root route role detection:', {
        userRole,
        currentUserRole: userStore.currentUser?.role,
        hasManagementAccess: hasManagementAccess(userRole),
        currentUser: userStore.currentUser ? {
          id: userStore.currentUser.id,
          email: userStore.currentUser.email,
          role: userStore.currentUser.role
        } : null
      })

      // GUIDE and MENTOR go to management pages
      if (hasManagementAccess(userRole)) {
        console.debug('[Router] Redirecting GUIDE/MENTOR user to /manage')
        return next({ path: '/manage', query: to.query })
      }
      // COMMON users go to permission denied
      else if (userRole === 'COMMON') {
        console.debug('[Router] Redirecting COMMON user to /permission-denied')
        return next('/permission-denied')
      }
      // FELLOW and others: check for personalized curriculum (use API as source of truth)
      else {
        if (!userRole && userStore.currentUser) {
          console.warn('[Router] User data exists but role could not be determined. User:', {
            id: userStore.currentUser.id,
            email: userStore.currentUser.email,
            role: userStore.currentUser.role,
            is_guide: userStore.currentUser.is_guide
          })
        }

        // Use campus API as source of truth (user.has_personal_curriculum can be stale)
        let hasCurriculum = false
        try {
          const { useCampusStore } = await import('@/stores/campus')
          hasCurriculum = await useCampusStore().checkHasPersonalCurriculum()
        } catch {
          // Fallback to user flag if API fails (e.g. network error)
          hasCurriculum = !!(userStore.currentUser as any)?.has_personal_curriculum
        }
        if (hasCurriculum) {
          console.debug('[Router] Fellow has PersonalCurriculum, redirecting to /home')
          return next({ path: '/home', query: to.query })
        }

        // Fellow without personal curriculum: check if in active expedition (old dashboard)
        const userId = userStore.currentUser?.id
        let hasActiveExpedition = false
        if (userId) {
          try {
            const { useExpeditionsStore } = await import('@/stores/expeditions')
            hasActiveExpedition = await useExpeditionsStore().checkHasActiveExpedition(userId)
          } catch {
            // Ignore errors, treat as no active expedition
          }
        }
        if (hasActiveExpedition) {
          console.debug('[Router] Fellow in active expedition (no PC), redirecting to /dashboard')
          return next({ path: '/dashboard', query: to.query })
        }

        // Fellow without curriculum and not in active expedition: send to campus setup
        console.debug('[Router] Fellow without curriculum or active expedition, redirecting to /curriculum/plan (setup)')
        return next({ path: '/curriculum/plan', query: to.query })
      }
    }
  },
  {
    path: '/dashboard',
    component: DashboardLayout,
    name: 'Dashboard Layout',
    meta: { requiresAuth: true, nav: true, requiresNonGuide: true },
    beforeEnter: async (_to: any, _from: any, next: any) => {
      try {
        const { useCampusStore } = await import('@/stores/campus')
        const hasCurriculum = await useCampusStore().checkHasPersonalCurriculum()
        if (hasCurriculum) {
          return next({ path: '/home' })
        }
      } catch {
        // If the check fails, allow through to the expedition dashboard
      }
      return next()
    },
    children: [
      {
        path: '',
        name: 'DashboardIndex',
        component: DashboardIndexView,
        meta: { title: 'My Dashboard' }
      }
    ]
  },
  {
    path: '/my-progress',
    component: DashboardLayout,
    meta: { requiresAuth: true, nav: true, requiresNonGuide: true },
    beforeEnter: async (_to: any, _from: any, next: any) => {
      try {
        const { useCampusStore } = await import('@/stores/campus')
        const hasCurriculum = await useCampusStore().checkHasPersonalCurriculum()
        if (hasCurriculum) {
          return next({ path: '/home' })
        }
      } catch {
        // If the check fails, allow through to the expedition view
      }
      return next()
    },
    children: [
      {
        path: '',
        name: 'MyProgressView',
        component: () => import('@/views/dashboard/DashboardProgressView.vue'),
        meta: { title: 'My Progress' }
      }
    ]
  },
  {
    path: '/my-expedition',
    component: DashboardLayout,
    meta: { requiresAuth: true, nav: true, requiresNonGuide: true },
    beforeEnter: async (_to: any, _from: any, next: any) => {
      try {
        const { useCampusStore } = await import('@/stores/campus')
        const hasCurriculum = await useCampusStore().checkHasPersonalCurriculum()
        if (hasCurriculum) {
          return next({ path: '/home' })
        }
      } catch {
        // If the check fails, allow through to the expedition view
      }
      return next()
    },
    children: [
      {
        path: '',
        name: 'UserExpeditionView',
        component: UserExpeditionView,
        meta: { title: 'My Expedition' }
      }
    ]
  },
  {
    path: '/manage',
    component: DashboardLayout,
    name: 'ManageIndex',
    meta: { requiresAuth: true, nav: true, requiresGuide: true },
    children: [
      {
        path: '',
        name: 'ManageIndexView',
        component: ManageIndexView,
        meta: { title: 'Management' }
      },
      {
        path: 'create',
        name: 'CreateExpeditionView',
        component: CreateExpeditionView,
        meta: { title: 'Create Expedition', requiresAdmin: true }
      },
      {
        path: 'create-pod',
        name: 'CreatePodView',
        component: CreatePodView,
        meta: { title: 'Create Pod', requiresAdmin: true }
      },
      {
        path: 'onboarding',
        name: 'OnboardingView',
        component: OnboardingView,
        meta: { title: 'Onboarding', requiresAdmin: true }
      },
      {
        path: 'pod/:id',
        component: PodManagementLayout,
        meta: { title: 'Pod Management' },
        children: [
          {
            path: '',
            name: 'ManagePodView',
            component: () => import('@/views/manage/pod/PodProgramView.vue'),
            meta: { title: 'Pod Program' }
          },
          {
            path: 'curriculum',
            name: 'PodCurriculumView',
            component: () => import('@/views/manage/pod/PodCurriculumView.vue'),
            meta: { title: 'Pod Curriculum' }
          },
          {
            path: 'notes',
            name: 'PodNotesView',
            component: () => import('@/views/manage/pod/PodNotesView.vue'),
            meta: { title: 'Pod Weekly Notes' }
          },
          {
            path: 'members',
            name: 'PodMembersView',
            component: () => import('@/views/manage/pod/PodMembersView.vue'),
            meta: { title: 'Pod Members' }
          },
          {
            path: 'program-statuses',
            name: 'PodProgramStatusesView',
            component: () => import('@/views/manage/pod/PodProgramStatusesView.vue'),
            meta: { title: 'Pod Program Statuses' }
          },
          {
            path: 'job-applications',
            name: 'PodJobApplicationsView',
            component: () => import('@/views/manage/pod/PodJobApplicationsView.vue'),
            meta: { title: 'Pod Job Applications' }
          },
          {
            path: 'reports',
            name: 'PodReportsView',
            component: () => import('@/views/manage/pod/PodReportsView.vue'),
            meta: { title: 'Pod Reports' }
          },
          {
            path: 'comments',
            name: 'PodCommentsView',
            component: () => import('@/views/manage/pod/PodCommentsView.vue'),
            meta: { title: 'Pod Comments' }
          },
          {
            path: ':userId/fellow-settings',
            name: 'ManagePodFellowSettingsView',
            component: () => import('@/views/manage/campus/CampusFellowSettingsView.vue'),
            meta: { title: 'Fellow Settings', requiresAdmin: true }
          },
          {
            path: ':userId',
            name: 'ManagePodFellowView',
            component: () => import('@/views/manage/campus/ManageCampusFellowView.vue'),
            meta: { title: 'Fellow Progress' }
          },
        ]
      },
      {
        path: 'fellow/:id',
        name: 'ManageFellowProfileView',
        component: () => import('@/views/manage/ManageFellowProfileView.vue'),
        meta: { title: 'Fellow Profile' }
      },
      {
        path: 'tracks',
        name: 'ManageTracksView',
        component: () => import('@/views/manage/tracks/ManageTracksView.vue'),
        meta: { title: 'Tracks', requiresAdmin: true }
      },
      {
        path: 'tracks/create',
        name: 'ManageTrackCreateView',
        component: () => import('@/views/manage/tracks/ManageTrackFormView.vue'),
        meta: { title: 'Add Track', requiresAdmin: true }
      },
      {
        path: 'tracks/import-csv',
        name: 'ManageTrackImportCsvView',
        component: () => import('@/views/manage/tracks/ManageTrackImportCsvView.vue'),
        meta: { title: 'Create Track from CSV', requiresAdmin: true }
      },
      {
        path: 'tracks/:id',
        name: 'ManageTrackEditView',
        component: () => import('@/views/manage/tracks/ManageTrackFormView.vue'),
        meta: { title: 'Edit Track', requiresAdmin: true }
      },
      {
        path: ':id',
        name: 'ManageExpeditionParent',
        component: RouterViewWrapper,
        meta: { title: 'Expedition Management' },
        children: [
          {
            path: '',
            name: 'ManageExpeditionView',
            component: ManageExpeditionView,
            meta: { title: 'Expedition Management' }
          },
          {
            path: ':userId',
            name: 'ManageFellowParent',
            component: RouterView,
            meta: { title: 'Fellow Progress' },
            children: [
              {
                path: '',
                name: 'ManageExpeditionUserView',
                component: ManageExpeditionUserView,
                meta: { title: 'Fellow Progress' }
              },
              {
                path: 'fellow-settings',
                name: 'FellowSettingsView',
                component: FellowSettingsView,
                meta: { title: 'Fellow Settings', requiresAdmin: true }
              }
            ]
          },
          {
            path: 'curriculum',
            name: 'ManageExpeditionCurriculumView',
            component: ManageExpeditionCurriculumView,
            meta: { title: 'Expedition Tasks' }
          },
          {
            path: 'notes',
            name: 'ManageExpeditionNotesView',
            component: ManageExpeditionNotesView,
            meta: { title: 'Expedition Notes' }
          },
          {
            path: 'demo-mode',
            name: 'ManageExpeditionDemoModeView',
            component: DemoModeView
          },
          {
            path: 'members',
            name: 'ManageExpeditionMembersView',
            component: ManageExpeditionMembersView,
            meta: { title: 'Expedition Members' }
          },
          {
            path: 'program-statuses',
            name: 'ExpeditionProgramStatusesView',
            component: ExpeditionProgramStatusesView,
            meta: { title: 'Program Statuses' }
          },
          {
            path: 'job-applications',
            name: 'ExpeditionJobApplicationsView',
            component: ExpeditionJobApplicationsView,
            meta: { title: 'Job Applications' }
          },
          {
            path: 'reports',
            name: 'ExpeditionReportsView',
            component: ExpeditionReportsView,
            meta: { title: 'Reports & Strikes' }
          },
          {
            path: 'comments',
            name: 'ExpeditionCommentsView',
            component: ExpeditionCommentsView,
            meta: { title: 'Internal Comments' }
          },
        ]
      },
      {
        path: 'review-history/user/:userId',
        component: RouterViewWrapper,
        meta: { title: 'Fellow Review History' },
        children: [
          {
            path: '',
            name: 'FellowReviewHistoryView',
            component: FellowReviewHistoryView,
            meta: { title: 'Fellow Review History' }
          }
        ]
      },
      {
        path: 'graduation',
        component: ManageLayout,
        meta: { title: 'Graduation', requiresGuide: true },
        children: [
          {
            path: '',
            name: 'GraduationView',
            component: () => import('@/views/manage/GraduationView.vue'),
            meta: { title: 'Graduation' }
          }
        ]
      },
      // Old URLs, still linked from Slack messages.
      { path: 'graduating-fellows', redirect: '/manage/graduation?tab=graduating' },
      { path: 'completed-fellows', redirect: '/manage/graduation?tab=completed' },
      {
        path: 'fellows',
        component: ManageLayout,
        meta: { title: 'Manage Fellows', requiresAdmin: true },
        children: [
          {
            path: '',
            name: 'ManageAllAdventurersView',
            component: ManageAllAdventurersView,
            meta: { title: 'Manage Fellows', requiresAdmin: true }
          }
        ]
      },
      {
        path: 'reports',
        component: ManageLayout,
        meta: { title: 'Manage Fellows Reports' },
        children: [
          {
            path: '',
            name: 'ManageFellowsReportsView',
            component: ManageAllReportsView,
            meta: { title: 'Manage Fellows Reports' }
          },
          {
            path: ':reportId',
            name: 'ManageFellowsReportView',
            component: ManageAllReportsView,
            meta: { title: 'Manage Fellows Reports' }
          }
        ]
      },
      {
        path: 'program-statuses',
        component: ManageLayout,
        meta: { title: 'Manage Program Progress' },
        children: [
          {
            path: '',
            name: 'ManageProgramProgressView',
            component: ProgramStatusesView,
            meta: { title: 'Manage Program Progress' }
          }
        ]
      },
      {
        path: 'internal-comments',
        component: ManageLayout,
        meta: { title: 'Manage Internal Comments' },
        children: [
          {
            path: '',
            name: 'ManageAllInternalCommentsView',
            component: ManageAllInternalCommentsView,
            meta: { title: 'Manage Internal Comments' }
          }
        ]
      }
    ]
  },
  {
    path: '/account',
    component: ProfileAccountLayout,
    meta: { requiresAuth: true, nav: true },
    children: [
      {
        path: '',
        name: 'AccountView',
        component: AccountView,
        meta: { title: 'Account Settings' }
      }
    ]
  },
  {
    path: '/profile',
    beforeEnter: () => { window.location.href = 'https://outtalent.com/profile'; return false },
    component: ProfileAccountLayout
  },
  {
    path: '/profile/edit',
    beforeEnter: () => { window.location.href = 'https://outtalent.com/profile/edit'; return false },
    component: ProfileAccountLayout
  },
  {
    path: '/profile/:username',
    beforeEnter: (to: RouteLocationNormalized) => {
      window.location.href = `https://outtalent.com/profile/${to.params.username}`
      return false
    },
    component: ProfileAccountLayout
  },
  {
    path: '/profile/:username/edit',
    beforeEnter: (to: RouteLocationNormalized) => {
      window.location.href = `https://outtalent.com/profile/${to.params.username}/edit`
      return false
    },
    component: ProfileAccountLayout
  },
  // Graduation form — Campus (Pod) members see it in CampusLayout
  {
    path: '/graduation',
    component: CampusLayout,
    meta: { requiresAuth: true, requiresNonGuide: true },
    beforeEnter: async (_to: any, _from: any, next: any) => {
      // Pod members use CampusLayout regardless of whether they have a curriculum yet.
      // Use is_pod_member from the current-user response as the authoritative check.
      const { useUserStore } = await import('@/stores/user')
      const userStore = useUserStore()
      if (!userStore.currentUser) {
        try { await userStore.fetchCurrentUser() } catch { /* fall through */ }
      }
      const isPodMember = userStore.currentUser?.is_pod_member ?? false
      if (!isPodMember) return next({ path: '/graduation-expedition' })
      return next()
    },
    children: [
      {
        path: '',
        name: 'GraduationFormCampus',
        component: () => import('@/views/GraduationFormView.vue'),
        meta: { title: 'Graduation' }
      }
    ]
  },
  // Graduation form — Expedition members see it in DashboardLayout
  {
    path: '/graduation-expedition',
    component: DashboardLayout,
    meta: { requiresAuth: true, requiresNonGuide: true },
    children: [
      {
        path: '',
        name: 'GraduationForm',
        component: () => import('@/views/GraduationFormView.vue'),
        meta: { title: 'Graduation' }
      }
    ]
  },
  {
    path: '/goals',
    component: CampusLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'ProgramGoals',
        component: () => import('@/views/campus/ProgramGoalsView.vue'),
        meta: { title: 'My Goals' }
      }
    ]
  },
  // Personalized Curriculum (Campus) routes
  {
    path: '/home',
    component: CampusLayout,
    meta: { requiresAuth: true, requiresNonGuide: true },
    children: [
      {
        path: '',
        name: 'PersonalizedHome',
        component: () => import('@/views/campus/PersonalizedHomeView.vue'),
        meta: { title: 'Home' }
      }
    ]
  },
  {
    path: '/curriculum',
    component: CampusLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Curriculum',
        redirect: '/curriculum/plan',
      },
      {
        path: 'plan',
        name: 'CurriculumPlan',
        component: () => import('@/views/campus/CurriculumPlanView.vue'),
        meta: { title: 'Your Curriculum Plan' }
      },
      {
        path: 'tasks',
        name: 'PersonalizedCurriculum',
        component: () => import('@/views/campus/PersonalizedCurriculumView.vue'),
        meta: { title: 'My Tasks (by Week)' }
      },
      {
        path: 'tasks/list',
        name: 'TaskList',
        component: () => import('@/views/campus/TaskListView.vue'),
        meta: { title: 'All Tasks' }
      }
    ]
  },
  {
    path: '/board',
    component: CampusLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'TaskBoard',
        component: () => import('@/views/campus/TaskBoardView.vue'),
        meta: { title: 'Task Board' }
      }
    ]
  },
  {
    path: '/tracks',
    component: CampusLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'AllTracks',
        component: () => import('@/views/campus/AllTracksView.vue'),
        meta: { title: 'All Tracks' }
      },
      {
        path: ':id',
        name: 'TrackDetail',
        component: () => import('@/views/campus/TrackDetailView.vue'),
        meta: { title: 'Track Detail' }
      }
    ]
  },
  {
    path: '/community',
    component: CampusLayout,
    meta: { requiresAuth: true, requiresNonGuide: true },
    children: [
      {
        path: '',
        name: 'Community',
        component: { template: '<div class="max-w-3xl mx-auto px-4 py-8"><h1 class="text-2xl font-bold text-gray-900">Community</h1><p class="text-gray-500 mt-2">Coming soon.</p></div>' },
        meta: { title: 'Community' }
      }
    ]
  },
  // Legacy URL (guides' bookmarks): redirect to campus fellow route
  {
    path: '/manage/pathway/fellows/:userId',
    redirect: (to: RouteLocationGeneric) =>
      `/manage/campus/fellows/${to.params.userId}`,
  },
  // Guide: Campus fellow detail
  {
    path: '/manage/campus/fellows/:userId',
    component: DashboardLayout,
    meta: { requiresAuth: true, requiresGuide: true },
    children: [
      {
        path: '',
        name: 'ManageCampusFellowView',
        component: () => import('@/views/manage/campus/ManageCampusFellowView.vue'),
        meta: { title: 'Fellow Progress' }
      },
      {
        path: 'fellow-settings',
        name: 'CampusFellowSettingsView',
        component: () => import('@/views/manage/campus/CampusFellowSettingsView.vue'),
        meta: { title: 'Fellow Settings', requiresAdmin: true }
      },
    ]
  },
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsView,
    meta: { requiresAuth: true }
  },
  // Old guest tuition links (/tuition/<plan id>): guest checkout was removed,
  // every plan is bought while logged in.
  {
    path: `${APP_ROUTES.TUITION}/:id`,
    redirect: APP_ROUTES.TUITION
  },
  // Authenticated subscription route
  {
    path: APP_ROUTES.TUITION,
    component: DashboardLayout,
    name: 'Subscription Layout',
    meta: { requiresAuth: true, title: 'Choose Plan' },
    children: [
      {
        path: '',
        name: 'Subscription',
        component: SubscriptionView,
        meta: { title: 'Choose Plan' }
      }
    ]
  },
  {
    path: APP_ROUTES.TUITION_MANAGE,
    component: ProfileAccountLayout,
    name: 'Subscription Manage Layout',
    meta: { requiresAuth: true, title: 'Tuition Management' },
    children: [
      {
        path: '',
        name: 'SubscriptionManage',
        component: SubscriptionManageView,
        meta: { title: 'Tuition Management' }
      }
    ]
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { title: 'Login' }
  },
  {
    path: '/sign-in',
    name: 'SignIn',
    component: LoginView,
    meta: { title: 'Sign In' }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: { title: 'Register' }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: AuthCallbackView,
    meta: { title: 'Authenticating...' }
  },
  {
    path: '/complete-profile',
    name: 'CompleteProfile',
    component: CompleteProfileView,
    meta: { requiresAuth: false, allowIncompleteProfile: true, title: 'Complete Your Profile' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordView,
    meta: { title: 'Forgot Password' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordView,
    props: true, // Accept the token as a prop
    meta: { title: 'Reset Password' }
  },
  {
    path: '/permission-denied',
    name: 'PermissionDenied',
    component: PermissionDeniedView,
    meta: { title: 'Permission Denied' }
  },
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditions',
    component: TermsAndConditionsView,
    meta: { title: 'Terms and Conditions' }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicyView,
    meta: { title: 'Privacy Policy' }
  },
  {
    path: '/dashboard/progress',
    redirect: '/my-progress'
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
    meta: { title: 'Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Only restore saved position, don't do any automatic scrolling
    if (savedPosition) {
      return savedPosition
    }
    // Don't scroll automatically for other cases
    return false
  }
})

// Carry the blur (privacy mode) query param across page navigations.
// Do not apply on same-path updates (e.g. toggling blur) to avoid loops.
router.beforeEach((to, from) => {
  if (from.query.blur && !to.query.blur && to.path !== from.path) {
    return { ...to, query: { ...to.query, blur: from.query.blur } }
  }
})

// Navigation guard to check authentication and roles
// eslint-disable-next-line complexity
router.beforeEach(async (to, from, next) => {
  const publicPages = [
    'Login',
    'Register',
    'ForgotPassword',
    'ResetPassword',
    'NotFound',
    'PermissionDenied',
    'TermsAndConditions',
    'PrivacyPolicy',
    'SignIn',
    'AuthCallback',
    'CompleteProfile'
  ]

  const authRequired = !publicPages.includes(to.name as string)
  const token = localStorage.getItem('token')
  const userStore = useUserStore()

  // Check authentication via userStore.currentUser (works with cookies) OR token
  // After login, userStore.currentUser is set even if token wasn't stored
  let isAuthenticated = !!userStore.currentUser || !!token

  // If we need auth but don't have user data, try to fetch it.
  // In cross-subdomain scenarios localStorage has no token but httpOnly
  // cookies (and the readable user_role cookie) may still be valid.
  const mayHaveCookieSession = !!getRoleFromCookie()
  if (authRequired && !userStore.currentUser && (token || from.name === 'Login' || mayHaveCookieSession)) {
    try {
      await userStore.fetchCurrentUser()
      isAuthenticated = true
    } catch (err) {
      console.error('Failed to fetch user data in router guard:', err)
      if (err && typeof err === 'object' && 'response' in err) {
        const httpErr = err as any
        if (httpErr.response?.status === 401 || httpErr.response?.status === 403) {
          isAuthenticated = false
          localStorage.removeItem('token')
          localStorage.removeItem('refresh_token')
          if (authRequired) {
            savePostLoginRedirect(to.fullPath)
            return next({ name: 'Login', query: { redirect: to.fullPath } })
          }
        }
      }
      if (!token) {
        isAuthenticated = false
      }
    }
  }

  const userRole = await getUserRole(userStore)

  if (isAuthenticated && ['Login', 'Register', 'ForgotPassword', 'ResetPassword', 'SignIn'].includes(to.name as string)) {
    return next({ name: 'root' })
  }

  if (authRequired && !isAuthenticated) {
    savePostLoginRedirect(to.fullPath)
    return next({ name: 'Login', query: { redirect: to.fullPath } })
  }

  // COMMON users can only access public pages
  if (isAuthenticated && userRole === 'COMMON' && !publicPages.includes(to.name as string)) {
    return next({ name: 'PermissionDenied' })
  }

  // Check for guide-only routes (GUIDE and MENTOR have access)
  if (to.meta.requiresGuide && !hasManagementAccess(userRole)) {
    return next({ path: from.path || '/' })
  }

  // Check for admin-only routes
  if (to.meta.requiresAdmin && !isUserAdmin()) {
    return next({ path: '/manage' })
  }

  // Check for non-guide routes (prevent GUIDE/MENTOR from accessing adventurer pages)
  if (to.meta.requiresNonGuide && hasManagementAccess(userRole)) {
    return next({ path: '/' }) // Redirect to root which will then redirect to /manage
  }

  // Subscription + platform access (GET /subscriptions/access/status/: has_access, platforms)
  if (isAuthenticated && !shouldSkipPlatformAccessCheck(to)) {
    const subAccess = useSubscriptionAccessStore()
    if (!subAccess.isCacheFresh()) {
      await subAccess.checkStatus(false)
    }
    if (!subAccess.hasAccess) {
      return next({ name: 'PermissionDenied' })
    }
    const requiredPlatform = getRequiredPlatformForRoute(to)
    if (!subAccess.hasPlatform(requiredPlatform)) {
      return next({ name: 'PermissionDenied' })
    }
  }

  const pageTitle = to.meta.title ? `${to.meta.title} | Outtalent Campus` : 'Campus | Outtalent'
  document.title = pageTitle as string

  // Continue with the navigation
  next()
})

if (import.meta.env.VITE_POSTHOG_KEY) {
  router.afterEach((to) => {
    posthog.capture('$pageview', { path: to.fullPath })
  })
}

export default router
